import {useRef, useEffect, useCallback, useState} from 'react'
import {Box} from '@primer/react-brand'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {PlayIcon, PauseIcon} from '../_components/AutoPlayVideo/Icons'

type Props = {
  description?: string
  posterSrcLarge?: string
  posterSrcSmall?: string
  videoSrcLarge: string
  videoSrcSmall?: string
  className?: string
}

// Convert HTTP URLs to HTTPS for CSP compliance
const ensureHttps = (url?: string) => {
  if (!url) return url
  return url.startsWith('//') ? `https:${url}` : url.replace(/^http:/, 'https:')
}

export function HeroVideo(props: Props) {
  const {description = '', posterSrcLarge, posterSrcSmall, videoSrcLarge, videoSrcSmall, className} = props

  // Convert URLs to HTTPS for CSP compliance
  const httpsVideoSrcLarge = ensureHttps(videoSrcLarge)
  const httpsVideoSrcSmall = ensureHttps(videoSrcSmall)
  const httpsPosterSrcLarge = ensureHttps(posterSrcLarge)
  const httpsPosterSrcSmall = ensureHttps(posterSrcSmall)

  const videoLgRef = useRef<HTMLVideoElement>(null)
  const videoSmRef = useRef<HTMLVideoElement>(null)
  const [videoState, setVideoState] = useState('playing')
  const [videoButtonLabel, setVideoButtonLabel] = useState('Pause')
  const [videoButtonPressed, setVideoButtonPressed] = useState(false)
  const [videoButtonAriaLabel, setVideoButtonAriaLabel] = useState('Pause video')
  const [VideoIcon, setVideoIcon] = useState(() => PauseIcon)

  const setPausedButtonState = useCallback(() => {
    setVideoState('paused')
    setVideoButtonLabel('Play')
    setVideoButtonPressed(false)
    setVideoButtonAriaLabel('Play video')
    setVideoIcon(() => PlayIcon)
  }, [])

  const setPlayingButtonState = useCallback(() => {
    setVideoState('playing')
    setVideoButtonLabel('Pause')
    setVideoButtonPressed(true)
    setVideoButtonAriaLabel('Pause video')
    setVideoIcon(() => PauseIcon)
  }, [])

  const setReplayButtonState = useCallback(() => {
    setVideoState('ended')
    setVideoButtonLabel('Replay')
    setVideoButtonPressed(false)
    setVideoButtonAriaLabel('Replay video')
    setVideoIcon(() => PlayIcon)
  }, [])

  const introVideoPlay = useCallback(() => {
    const playPromiseLg = videoLgRef.current?.play()
    // eslint-disable-next-line github/no-then
    if (playPromiseLg !== undefined) playPromiseLg.catch(() => {})

    const playPromiseSm = videoSmRef.current?.play()
    // eslint-disable-next-line github/no-then
    if (playPromiseSm !== undefined) playPromiseSm.catch(() => {})
  }, [])

  const introVideoPause = useCallback(() => {
    videoLgRef.current?.pause()
    videoSmRef.current?.pause()
  }, [])

  const handleVideoStateChange = useCallback(() => {
    if (!videoLgRef.current || !videoSmRef.current) return

    if (videoState === 'playing') {
      setPausedButtonState()
      introVideoPause()
      return
    }

    setPlayingButtonState()
    if (videoState === 'ended') {
      videoLgRef.current.currentTime = 0
      videoSmRef.current.currentTime = 0
    }

    introVideoPlay()
  }, [introVideoPause, introVideoPlay, setPausedButtonState, setPlayingButtonState, videoState])

  // Auto-play on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!videoLgRef.current || !videoSmRef.current) return
      setPlayingButtonState()
      introVideoPlay()
    }, 0)

    return () => clearTimeout(timer)
  }, [introVideoPlay, setPlayingButtonState])

  return (
    <Box className={`position-relative ${className || ''}`}>
      <Box role="img" className="overflow-hidden rounded-3" aria-label={description}>
        <video
          playsInline
          muted
          className="lp-Hero-video lp-Hero-video--landscape width-full height-auto"
          ref={videoLgRef}
          poster={httpsPosterSrcLarge}
          onEnded={() => setReplayButtonState()}
          style={{
            aspectRatio: '16/9',
            maxWidth: '100%',
          }}
        >
          <source src={httpsVideoSrcLarge} type="video/mp4" />
        </video>

        {videoSrcSmall ? (
          <video
            playsInline
            muted
            className="lp-Hero-video lp-Hero-video--portrait width-full height-auto"
            ref={videoSmRef}
            poster={httpsPosterSrcSmall}
            onEnded={() => setReplayButtonState()}
            style={{
              aspectRatio: '16/9',
              maxWidth: '100%',
            }}
          >
            <source src={httpsVideoSrcSmall} type="video/mp4" />
          </video>
        ) : null}
      </Box>

      <button
        className="lp-Hero-videoPlayerButton PlayButton PlayButton--gray"
        onClick={handleVideoStateChange}
        aria-pressed={videoButtonPressed}
        aria-label={videoButtonAriaLabel}
        {...getAnalyticsEvent({
          action: videoButtonLabel.toLowerCase(),
          tag: 'button',
          context: 'demo_gif',
          location: 'hero',
        })}
      >
        <VideoIcon />

        <span className="sr-only">{videoButtonLabel}</span>
      </button>
    </Box>
  )
}

try{ HeroVideo.displayName ||= 'HeroVideo' } catch {}